import { yupResolver } from '@hookform/resolvers/yup';
import type React from 'react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { boolean, object, string } from 'yup';

import useAppNavigation from '@/hooks/useAppNavigation';
import { isNil } from 'lodash-es';
import { ROUTES } from '../../../constants/Constants';
import useLoginModal from '../../../hooks/useLoginModal';
import { earlyAccessRequest } from '../../../redux/ducks/PrelaunchDucks';
import type { EarlyAccessRequestType } from '../../../redux/ducks/PrelaunchDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/EnvironmentHelper';
import Button from '../../button/Button';
import Input from '../../inputs/Input';
import Footer from '../../layout/Footer';
import LazyBackground from '../../layout/LazyBackground/LazyBackground';

const formSchema = object().shape({
  email: string().email('Email not valid').required('Email is required'),
  terms: boolean()
    .oneOf([true], 'Please check our Privacy Policy and Terms of Use')
    .required('Please check our Privacy Policy and Terms of Use'),
});

type InvitationFormInput = {
  email: string;
  terms: boolean;
};

interface PrelaunchProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const Prelaunch: React.FC<PrelaunchProps> = ({ ssrFetchedData }) => {
  const [emailEntered, setEmailEntered] = useState(false);
  const gdprCompliant = true;
  const [sendingEmail, setSendingEmail] = useState(false);
  const dispatch = useDispatch();
  const loginModal = useLoginModal();
  const earlyAccessResponse = useSelector(
    (state: RootReducerType) => state.prelaunch.earlyAccessResponse,
  );
  const { t, i18n } = useTranslation();
  const { toLocalizedRoute } = useAppNavigation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InvitationFormInput>({
    resolver: yupResolver(formSchema),
  });

  const onSubmit: SubmitHandler<InvitationFormInput> = (data) => {
    setSendingEmail(true);

    // dispatch on submit action
    const payload: EarlyAccessRequestType = {
      email: data.email,
      language: i18n.language,
    };

    dispatch(earlyAccessRequest(payload));
  };

  useEffect(() => {
    if (earlyAccessResponse && sendingEmail) {
      setSendingEmail(false);

      if (earlyAccessResponse?.data) {
        // email saved correctly, not yet suported by the backend
      }

      setEmailEntered(true);
    }
  }, [earlyAccessResponse, sendingEmail]);

  const backgroundImage =
    ssrFetchedData.appConfiguration !== null
      ? ssrFetchedData.appConfiguration.media.filter(
          (media) => media.id === 'prelaunch_background',
        )[0].url
      : undefined;

  const cdnDomain = getEnvironmentVariable('REACT_APP_CDN_API');

  return (
    <LazyBackground
      className="flex min-h-[calc(100dvh)] flex-col overflow-y-hidden bg-slate-200 bg-cover bg-fixed bg-no-repeat"
      src={!isNil(backgroundImage) ? `//${cdnDomain}${backgroundImage}` : ''}
    >
      <div className="flex min-h-full w-full grow flex-col justify-between overflow-y-auto">
        <div className="mt-4 flex h-full flex-col items-center px-4 sm:px-6 lg:px-8 2xl:items-end">
          <div className="my-auto w-full max-w-screen-lg rounded-md bg-white/75 px-6 py-12 shadow-md backdrop-blur-md transition ease-in-out hover:shadow-lg">
            {!gdprCompliant ? (
              <div>
                <div className="text-pretty gap-4 flex flex-col">
                  <h1 className="text-4xl font-extrabold text-gray-900 text-right drop-shadow-sm">
                    {t('dynamic.prelaunch_title')}
                  </h1>
                  <h2 className="mt-3 text-lg text-justify text-gray-500">
                    {t('dynamic.prelaunch_subtitle')}
                  </h2>
                </div>
                <div className="mt-10 flex flex-col items-center">
                  <p className="mx-2 text-justify text-lg text-gray-500">
                    <Trans i18nKey="static.prelaunch.beta_welcome_disclaimer">
                      <span className="font-light text-sky-600">
                        <a
                          data-test-id="discord-link"
                          href="https://discord.com/channels/1194693419923157142/1194693419923157145"
                          target="_blank"
                          rel="noreferrer"
                        >
                          &nbsp;
                        </a>
                      </span>
                    </Trans>
                  </p>
                </div>
              </div>
            ) : !emailEntered ? (
              <div>
                <div className="text-pretty gap-4 flex flex-col">
                  <h1 className="text-4xl font-extrabold text-gray-900 text-right drop-shadow-sm">
                    {t('dynamic.prelaunch_title')}
                  </h1>
                  <h2 className="mt-3 text-lg text-justify text-gray-500">
                    {t('dynamic.prelaunch_subtitle')}
                  </h2>
                </div>
                <div className="mt-10 flex flex-col items-center">
                  <div className="w-full max-w-screen-sm">
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="mx-4 flex flex-col items-center border-b border-gray-500 py-2">
                        <div className="w-full">
                          <Input
                            id="email"
                            type="email"
                            disabled={sendingEmail}
                            label={t('static.prelaunch.enter_email')}
                            register={register}
                            errors={errors}
                            options={{
                              required: true,
                            }}
                          />
                        </div>
                        <p className="mt-2 flex size-full flex-row justify-start text-center text-xs text-gray-500">
                          <span className="ml-1 mr-2 inline-block align-middle">
                            <input
                              data-test-id="prelaunch-accept-conditions"
                              aria-label={t(
                                'static.prelaunch.accept_disclaimer_label',
                              )}
                              type="checkbox"
                              {...register('terms')}
                              className="peer relative mt-1 size-4 shrink-0 appearance-auto rounded border-2 border-gray-200 bg-white
        checked:border-0 checked:bg-sky-600 focus:outline-none focus:ring-1
        focus:ring-sky-100 focus:ring-offset-0"
                            />
                          </span>
                          <span
                            className={`flex flex-row items-center justify-normal ${
                              errors.terms ? 'text-red-400' : null
                            }`}
                          >
                            <span>
                              <Trans i18nKey="static.prelaunch.accept_disclaimer">
                                <span className="cursor-pointer font-semibold hover:text-sky-600">
                                  <a
                                    data-test-id="terms-conditions-link"
                                    href={toLocalizedRoute(
                                      ROUTES.PRIVACY_POLICY_ROUTE,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    &nbsp;
                                  </a>
                                </span>
                                <span className="cursor-pointer font-semibold hover:text-sky-600">
                                  <a
                                    data-test-id="terms-conditions-link"
                                    href={toLocalizedRoute(
                                      ROUTES.TERMS_AND_CONDITIONS_ROUTE,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    &nbsp;
                                  </a>
                                </span>
                              </Trans>
                            </span>
                          </span>
                        </p>
                        <div className="mt-4 w-full">
                          <Button
                            disabled={sendingEmail}
                            loading={sendingEmail}
                            label={t('static.prelaunch.early_access_button')}
                            onClick={handleSubmit(onSubmit)}
                            data-test-id="modal-button"
                            id="prelaunch-early-access-button"
                            className="uppercase"
                          />
                        </div>
                      </div>
                    </form>
                  </div>
                  <p className="mt-2 text-center text-xs text-gray-500">
                    {t('static.prelaunch.beta_disclaimer')}
                  </p>
                </div>
                <div className="mt-6 flex flex-col items-center font-light">
                  <div className="mt-2 size-full justify-center text-center text-gray-500">
                    <Trans i18nKey="static.prelaunch.already_account">
                      <span className="cursor-pointer font-semibold hover:text-sky-600">
                        <button
                          id={t('static.navbar.usermenu_login_label')}
                          key={t('static.navbar.usermenu_login_label')}
                          aria-label={t('static.navbar.usermenu_login_label')}
                          data-test-id="login-button"
                          type="button"
                          onClick={loginModal.onOpen}
                        >
                          &nbsp;
                        </button>
                      </span>
                    </Trans>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-center">
                  <h2 className="mx-4 text-4xl font-extrabold text-gray-900">
                    {t('static.prelaunch.success_title')}
                  </h2>
                  <p className="mx-2 mt-12 text-justify text-lg text-gray-500">
                    <Trans
                      i18nKey="static.prelaunch.success_first_text"
                      values={{ name: t('dynamic.common_company_name') }}
                    >
                      <span className="font-bold text-gray-900">&nbsp;</span>
                    </Trans>
                  </p>

                  <p className="mx-2 mt-12 text-justify text-lg text-gray-500">
                    <Trans i18nKey="static.prelaunch.success_second_text">
                      <span className="font-light text-sky-600">
                        <a
                          data-test-id="prelaunch-early-access-discord-link"
                          href="https://discord.com/channels/1194693419923157142/1194693419923157145"
                          target="_blank"
                          rel="noreferrer"
                          id="prelaunch-early-access-discord-link"
                          data-umami-event="prelaunch-early-access-discord-link"
                        >
                          &nbsp;
                        </a>
                      </span>
                    </Trans>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <Footer ssrFetchedData={ssrFetchedData} />
      </div>
    </LazyBackground>
  );
};

export default Prelaunch;
